import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Search, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { diagnosticsAPI, entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Entity Account Trace diagnostic card.
 *
 * Input: pick an entity from the same source the picker uses.
 * Output: the entity's UUID/slug/aliases as resolved; the exact filter
 * the Bank Balances endpoint would run; count of matching bank-account
 * mappings; full distribution of entity_id values across ALL mappings
 * with best-effort resolution and orphan flagging.
 */
export function EntityAccountTracePanel() {
  var [entities, setEntities] = useState([]);
  var [selectedId, setSelectedId] = useState('');
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);

  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, []);

  var runTrace = async function () {
    if (!selectedId) return;
    setLoading(true);
    setResult(null);
    try {
      var res = await diagnosticsAPI.entityAccountTrace(selectedId);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Search className="h-4 w-4" />
          Entity Account Trace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <select
            className="text-sm border rounded px-2 py-1.5 bg-background flex-1"
            value={selectedId}
            onChange={function (e) { setSelectedId(e.target.value); }}
          >
            <option value="">Select entity...</option>
            {entities.map(function (ent) {
              return <option key={ent.id} value={ent.id}>{ent.name}</option>;
            })}
          </select>
          <Button size="sm" onClick={runTrace} disabled={loading || !selectedId}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Trace'}
          </Button>
        </div>

        {result && (
          <div className="space-y-4 text-sm">
            {/* Resolution */}
            <div>
              <h4 className="font-semibold mb-1">Resolution</h4>
              <div className="bg-muted/50 rounded p-3 space-y-1 font-mono text-xs">
                <div>Input: {result.resolution.input}</div>
                {result.resolution.normalized !== result.resolution.input && (
                  <div>Normalized: {result.resolution.normalized}
                    {result.resolution.is_retired_alias && (
                      <Badge variant="outline" className="ml-2 text-amber-600 border-amber-400">retired alias</Badge>
                    )}
                  </div>
                )}
                {result.resolution.resolved ? (
                  result.resolution.source && !result.resolution.canonical_name ? (
                    <>
                      <div>Name: {result.resolution.name || 'n/a'}</div>
                      <div>db type: {result.resolution.db_type || 'n/a'}</div>
                      <div>db UUID: {result.resolution.db_uuid || 'n/a'}</div>
                      <div className="text-amber-600">Source: {result.resolution.source}</div>
                      {result.resolution.warning && (
                        <div className="text-amber-600 text-[10px]">{result.resolution.warning}</div>
                      )}
                      {result.resolution.retired && (
                        <Badge variant="outline" className="text-amber-600 border-amber-400">retired</Badge>
                      )}
                    </>
                  ) : (
                    <>
                      <div>Canonical: {result.resolution.canonical_name}</div>
                      <div>Slug: {result.resolution.slug}</div>
                      <div>UUID: {result.resolution.uuid}</div>
                      {result.resolution.db_uuid && result.resolution.db_uuid !== result.resolution.uuid && (
                        <div>db UUID: {result.resolution.db_uuid}</div>
                      )}
                      <div>Country: {result.resolution.country ?? 'none'}, Role: {result.resolution.group_role ?? 'none'}</div>
                      {result.resolution.jurisdiction !== undefined && (
                        <div>Jurisdiction: {result.resolution.jurisdiction ?? 'none'}, Currency: {result.resolution.currency ?? 'none'}</div>
                      )}
                      {result.resolution.aliases?.length > 0 && (
                        <div>Aliases: {result.resolution.aliases.join(', ')}</div>
                      )}
                      {result.resolution.source && (
                        <div className="text-amber-600">Source: {result.resolution.source}</div>
                      )}
                    </>
                  )
                ) : (
                  <div className="text-destructive font-semibold">NOT RESOLVED</div>
                )}
              </div>
            </div>

            {/* Filter and match count */}
            <div>
              <h4 className="font-semibold mb-1">Bank Account Filter</h4>
              <div className="bg-muted/50 rounded p-3 space-y-1 font-mono text-xs">
                <div>Filter: {result.filter_display}</div>
                <div className="flex items-center gap-2">
                  Matching visible: <Badge variant={result.matching_visible_accounts > 0 ? 'default' : 'destructive'}>
                    {result.matching_visible_accounts}
                  </Badge>
                  {result.matching_hidden_accounts > 0 && (
                    <>Hidden: <Badge variant="outline" className="text-amber-600 border-amber-400">{result.matching_hidden_accounts}</Badge></>
                  )}
                </div>
              </div>
            </div>

            {/* Matching accounts sample */}
            {result.matching_samples?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-1">Matching Accounts (sample)</h4>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">Account</th>
                        <th className="py-1 pr-2">Bank</th>
                        <th className="py-1 pr-2">entity_id</th>
                        <th className="py-1 pr-2">entity_uuid</th>
                        <th className="py-1 pr-2">Currency</th>
                        <th className="py-1 pr-2">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.matching_samples.map(function (acc) {
                        return (
                          <tr key={acc.id} className="border-b border-border/30">
                            <td className="py-1 pr-2 font-mono">{acc.account_name}</td>
                            <td className="py-1 pr-2">{acc.bank_name}</td>
                            <td className="py-1 pr-2 font-mono">{acc.entity_id}</td>
                            <td className="py-1 pr-2 font-mono truncate max-w-[120px]">{acc.entity_uuid || '-'}</td>
                            <td className="py-1 pr-2">{acc.currency}</td>
                            <td className="py-1 pr-2">{acc.source}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* entity_id distribution */}
            <div>
              <h4 className="font-semibold mb-1">
                entity_id Distribution (all {result.total_bank_accounts} accounts)
                {result.orphaned_entity_id_count > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {result.orphaned_entity_id_count} orphaned
                  </Badge>
                )}
              </h4>
              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">entity_id</th>
                      <th className="py-1 pr-2">Resolved Name</th>
                      <th className="py-1 pr-2">Count</th>
                      <th className="py-1 pr-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.entity_id_distribution?.map(function (row) {
                      return (
                        <tr key={row.entity_id} className="border-b border-border/30">
                          <td className="py-1 pr-2 font-mono">{row.entity_id}</td>
                          <td className="py-1 pr-2">{row.resolved_name || '-'}</td>
                          <td className="py-1 pr-2 font-mono">{row.account_count}</td>
                          <td className="py-1 pr-2">
                            {row.orphan ? (
                              <Badge variant="destructive" className="text-[10px]">
                                <AlertTriangle className="h-3 w-3 mr-0.5" />orphan
                              </Badge>
                            ) : row.is_retired_alias ? (
                              <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px]">retired alias</Badge>
                            ) : (
                              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* entity_uuid distribution */}
            {result.entity_uuid_distribution?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-1">
                  entity_uuid Distribution
                  {result.orphaned_entity_uuid_count > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {result.orphaned_entity_uuid_count} orphaned
                    </Badge>
                  )}
                </h4>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">entity_uuid</th>
                        <th className="py-1 pr-2">Resolved Name</th>
                        <th className="py-1 pr-2">Count</th>
                        <th className="py-1 pr-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.entity_uuid_distribution.map(function (row) {
                        return (
                          <tr key={row.entity_uuid} className="border-b border-border/30">
                            <td className="py-1 pr-2 font-mono truncate max-w-[200px]">{row.entity_uuid}</td>
                            <td className="py-1 pr-2">{row.resolved_name || '-'}</td>
                            <td className="py-1 pr-2 font-mono">{row.account_count}</td>
                            <td className="py-1 pr-2">
                              {row.orphan ? (
                                <Badge variant="destructive" className="text-[10px]">
                                  <AlertTriangle className="h-3 w-3 mr-0.5" />orphan
                                </Badge>
                              ) : row.retired ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px]">retired doc</Badge>
                              ) : (
                                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Unassigned accounts listing */}
            {result.unassigned_accounts?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-1">
                  Unassigned Accounts
                  <Badge variant="destructive" className="ml-2">
                    {result.unassigned_accounts.length}
                  </Badge>
                </h4>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">Account</th>
                        <th className="py-1 pr-2">Bank</th>
                        <th className="py-1 pr-2">Source</th>
                        <th className="py-1 pr-2">Currency</th>
                        <th className="py-1 pr-2">entity_id</th>
                        <th className="py-1 pr-2">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unassigned_accounts.map(function (acc) {
                        return (
                          <tr key={acc.id} className="border-b border-border/30">
                            <td className="py-1 pr-2 font-mono">{acc.account_name || '-'}</td>
                            <td className="py-1 pr-2">{acc.bank_name || '-'}</td>
                            <td className="py-1 pr-2">{acc.source || '-'}</td>
                            <td className="py-1 pr-2">{acc.currency || '-'}</td>
                            <td className="py-1 pr-2 font-mono">{acc.entity_id || '-'}</td>
                            <td className="py-1 pr-2">
                              <div className="flex gap-1">
                                {acc.hidden && (
                                  <Badge variant="outline" className="text-[10px] text-muted-foreground">hidden</Badge>
                                )}
                                {acc.retired && (
                                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px]">retired</Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
